import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { useState } from 'react';
import { useRouter } from 'next/navigation';
import type { MakeModel, MakeModelItem } from '@tac/vehicle-api-client/types';
import { MakeModelCommandMenu } from './make-model-command-menu';
import { getVehicleDetailsHrefFromNames } from '@/utils/get-vehicle-details-href';
import { StreamlineCheck } from '@tac/icons';
import RegoSearchBox from './rego-search-box';
import { SEARCH_TYPES } from './constants';
import { logTrackingEvent } from '@/utils/tracking';
import { type ClassValue } from 'clsx';
import { cn } from '@/lib/utils';

type ToggleState = 'make-model' | 'plate';

export type SearchV2Props = {
  title?: string;
  makesModels?: MakeModel[];
  className?: ClassValue;
};

export const SearchV2 = ({ title, makesModels = [], className }: SearchV2Props) => {
  const router = useRouter();
  const [activeToggle, setActiveToggle] = useState<ToggleState>('make-model');

  const handleToggleChange = (value: ToggleState) => {
    const selected = value === SEARCH_TYPES.MAKE_MODEL.value ? SEARCH_TYPES.MAKE_MODEL : SEARCH_TYPES.REGO;

    logTrackingEvent({
      event: 'interaction_click',
      component_name: 'radio',
      click_text: selected.label,
      click_url: '',
    });

    if (value) setActiveToggle(value);
  };

  const handleVehicleSelect = (value: { make: MakeModelItem; model: MakeModelItem } | null) => {
    if (!value) return;

    const { make, model } = value;

    logTrackingEvent({
      event: 'searchMakeModel',
      make,
      model,
    });

    router.push(getVehicleDetailsHrefFromNames({ make: make.description, model: model.description }));
  };

  return (
    <div
      className={cn(
        'mx-auto w-full min-w-[310px] max-w-[420px] md:w-[660px] md:max-w-none bg-greyscale-0 p-4 md:p-12 rounded-lg border-2 border-greyscale-200',
        className
      )}
    >
      <h2 className="mb-6 text-center text-3xl md:text-4xl">{title}</h2>
      <div className="flex flex-col items-center gap-4 ">
        <ToggleGroup
          className="grid-cols-2 gap-3 items-stretch"
          value={activeToggle}
          type="single"
          defaultValue="make-model"
          onValueChange={handleToggleChange}
        >
          <ToggleGroupItem className="flex justify-start min-w-0 whitespace-normal" value="make-model">
            {activeToggle === 'make-model' && <StreamlineCheck className="size-4" />}
            Vehicle Make & Model
          </ToggleGroupItem>
          <ToggleGroupItem className="flex justify-start min-w-0 whitespace-normal" value="plate">
            {activeToggle === 'plate' && <StreamlineCheck className="size-4" />}
            Victorian Number Plate
          </ToggleGroupItem>
        </ToggleGroup>
        {activeToggle === 'make-model' && (
          <MakeModelCommandMenu allMakesModels={makesModels} onValueChange={handleVehicleSelect} />
        )}
        {activeToggle === 'plate' && <RegoSearchBox />}
      </div>
    </div>
  );
};

export default SearchV2;
